import { useState, useEffect } from "react";
import { BookmarkSimple, MagnifyingGlass, FloppyDisk, TrashSimple, Warning, Clock, ArrowClockwise } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { api } from "../lib/api";

const rid = () => Math.random().toString(36).slice(2);
const withIds = (lvls) => (lvls || []).map((l) => ({ ...l, id: rid() }));

function usedSlots(levels) {
  const s = new Set();
  (levels || []).forEach((l) => Object.entries(l.columns || {}).forEach(([slot, cols]) => { if ((cols || []).length) s.add(Number(slot)); }));
  return [...s];
}

export function checkCompat(tpl, files) {
  const loaded = (files || []).length;
  if (tpl.sources && tpl.sources.length) {
    if (loaded !== tpl.sources.length) return { ok: false, reason: `Il modello usa ${tpl.sources.length} sorgenti, ne hai caricate ${loaded}` };
    for (const s of tpl.sources) {
      const f = (files || []).find((x) => x.slot === s.slot);
      if (!f) return { ok: false, reason: `Manca la sorgente ${s.slot}` };
      const missing = (s.columns || []).filter((c) => !(f.columns || []).includes(c));
      if (missing.length) return { ok: false, reason: `Colonne mancanti in "${f.name}": ${missing.slice(0, 6).join(", ")}` };
    }
    return { ok: true };
  }
  const slots = usedSlots(tpl.levels);
  if (!slots.length) return { ok: true };
  if (loaded !== slots.length) return { ok: false, reason: `Il modello usa ${slots.length} sorgenti, ne hai caricate ${loaded}` };
  for (const l of tpl.levels || []) {
    for (const [slot, cols] of Object.entries(l.columns || {})) {
      if (!(cols || []).length) continue;
      const f = (files || []).find((x) => x.slot === Number(slot));
      if (!f) return { ok: false, reason: `Manca la sorgente ${slot}` };
      const missing = cols.filter((c) => !(f.columns || []).includes(c));
      if (missing.length) return { ok: false, reason: `Colonne mancanti in "${f.name}": ${missing.join(", ")}` };
    }
  }
  return { ok: true };
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT") + " alle " + d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  } catch (e) { return "data sconosciuta"; }
}

export function ModelManager({ job, setJob, levels, setLevels, userId, onRecalled }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [name, setName] = useState("");
  const [inclCols, setInclCols] = useState(true);
  const [inclFilters, setInclFilters] = useState(true);
  const [forceTpl, setForceTpl] = useState(null);
  const [delTpl, setDelTpl] = useState(null);

  const loadTemplates = () => { if (userId) api.listTemplates(userId).then(setTemplates).catch(() => {}); };
  useEffect(() => { if (open) loadTemplates(); }, [open, userId]);

  const sourcesInfo = (tpl) => {
    if (tpl.sources && tpl.sources.length) {
      return tpl.sources.map((s) => ({ slot: s.slot, name: s.name, columns: s.columns || [] }));
    }
    const map = {};
    (tpl.levels || []).forEach((l) => Object.entries(l.columns || {}).forEach(([slot, cols]) => {
      if (!(cols || []).length) return;
      map[slot] = map[slot] || new Set();
      cols.forEach((c) => map[slot].add(c));
    }));
    return Object.keys(map).sort().map((slot) => ({ slot: Number(slot), name: `Fonte ${slot}`, columns: [...map[slot]] }));
  };

  const doApply = async (tpl, force) => {
    const compat = checkCompat(tpl, job.files);
    if (!compat.ok && !force) { setForceTpl({ tpl, reason: compat.reason }); return; }
    setLevels(withIds(tpl.levels || []));
    const body = { levels: tpl.levels || [] };
    if (tpl.type === "project") {
      if (tpl.output_columns?.length) body.output_columns = tpl.output_columns;
      if (tpl.column_filters && Object.keys(tpl.column_filters).length) body.column_filters = tpl.column_filters;
    }
    try { await api.updateJob(job.id, body); setJob({ ...job, ...body }); }
    catch (e) { toast.error("Errore nel richiamo del modello"); return; }
    if (onRecalled) onRecalled({ name: tpl.name, sources: sourcesInfo(tpl) });
    toast.success(`Modello "${tpl.name}" richiamato`);
    setForceTpl(null); setOpen(false);
  };

  const saveTpl = async () => {
    if (!name.trim()) return;
    if (!(levels && levels.length)) { toast.error("Configura almeno un livello di abbinamento prima di salvare"); return; }
    const type = (inclCols || inclFilters) ? "project" : "rules";
    const outCols = inclCols ? (job.output_columns || []) : [];
    const filters = inclFilters ? (job.column_filters || {}) : {};
    const sources = (job.files || []).map((f) => ({ slot: f.slot, name: f.name, columns: f.columns || [] }));
    try {
      await api.saveTemplate(userId, name.trim(), levels || [], type, outCols, filters, sources);
      toast.success("Modello salvato");
      setName(""); loadTemplates();
    } catch (e) { toast.error("Errore salvataggio modello"); }
  };

  const doDelete = async () => {
    if (!delTpl) return;
    try { await api.deleteTemplate(delTpl.id); toast.message("Modello eliminato"); loadTemplates(); }
    catch (e) { toast.error("Errore eliminazione"); }
    setDelTpl(null);
  };

  const matchType = (t) => {
    if (typeFilter === "all") return true;
    if (typeFilter === "rules") return t.type !== "project";
    if (typeFilter === "cols") return (t.output_columns || []).length > 0;
    if (typeFilter === "filters") return t.column_filters && Object.keys(t.column_filters).length > 0;
    return true;
  };
  const shown = templates.filter((t) => t.type !== "tool" && matchType(t) && (!q || t.name.toLowerCase().includes(q.toLowerCase())));

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="model-manager-btn" className="gap-2">
        <BookmarkSimple size={16} /> Modelli
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover border-border max-w-2xl" data-testid="model-manager-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BookmarkSimple size={20} /> Gestione modelli</DialogTitle>
            <DialogDescription>Salva e richiama abbinamenti, colonne e filtri. I modelli sono condivisi tra i lavori.</DialogDescription>
          </DialogHeader>

          {/* Save panel */}
          <div className="rounded-xl border border-border p-3 bg-card space-y-2">
            <div className="text-xs font-semibold text-primary flex items-center gap-1"><FloppyDisk size={14} /> Salva lavoro corrente</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome modello (es. CF + Premio)"
              data-testid="model-name-input" className="bg-muted" onKeyDown={(e) => e.key === "Enter" && saveTpl()} />
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="model-incl-cols">
                <Checkbox checked={inclCols} onCheckedChange={(v) => setInclCols(!!v)} /> Includi colonne output
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="model-incl-filters">
                <Checkbox checked={inclFilters} onCheckedChange={(v) => setInclFilters(!!v)} /> Includi filtri
              </label>
              <Button size="sm" onClick={saveTpl} data-testid="model-save-btn" className="ml-auto gap-1"><FloppyDisk size={14} /> Salva</Button>
            </div>
          </div>

          {/* Search + list */}
          <div className="flex items-center gap-1 border border-border rounded-lg px-2 bg-muted">
            <MagnifyingGlass size={14} className="text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="model-search"
              placeholder="Cerca modello..." className="flex-1 bg-transparent text-sm outline-none py-2" />
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            {[["all", "Tutti"], ["rules", "Solo abbinamento"], ["cols", "Con colonne"], ["filters", "Con filtri"]].map(([k, lbl]) => (
              <button key={k} onClick={() => setTypeFilter(k)} data-testid={`model-type-${k}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${typeFilter === k ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {lbl}
              </button>
            ))}
          </div>

          <ScrollArea className="h-64">
            <div className="space-y-2 pr-2">
              {shown.length === 0 && <div className="text-xs text-muted-foreground py-4 text-center">Nessun modello</div>}
              {shown.map((t) => {
                const compat = checkCompat(t, job.files);
                return (
                  <div key={t.id} data-testid={`model-row-${t.id}`}
                    className={`rounded-xl border p-3 flex items-center gap-3 transition-colors ${compat.ok ? "border-border bg-card" : "border-border/40 bg-muted/30 opacity-60"}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold truncate">{t.name}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-border text-muted-foreground">{t.type === "project" ? "progetto" : "regole"}</span>
                        {!compat.ok && <span className="text-[10px] flex items-center gap-1 text-[hsl(var(--gold))]" title={compat.reason}><Warning size={11} weight="fill" /> non compatibile</span>}
                      </div>
                      <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5"><Clock size={11} /> creato il {fmtDate(t.created_at)}</div>
                      {!compat.ok && <div className="text-[10px] text-muted-foreground truncate">{compat.reason}</div>}
                    </div>
                    <Button size="sm" variant={compat.ok ? "default" : "outline"} data-testid={`model-recall-${t.id}`}
                      onClick={() => doApply(t, false)} className="gap-1 shrink-0">
                      <ArrowClockwise size={13} /> Richiama
                    </Button>
                    <button data-testid={`model-delete-${t.id}`} onClick={() => setDelTpl(t)}
                      className="text-muted-foreground hover:text-destructive shrink-0"><TrashSimple size={16} /></button>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Force recall confirm */}
      <Dialog open={!!forceTpl} onOpenChange={(o) => { if (!o) setForceTpl(null); }}>
        <DialogContent className="bg-popover border-border max-w-md" data-testid="model-force-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Warning size={20} className="text-[hsl(var(--gold))]" /> Modello non compatibile</DialogTitle>
            <DialogDescription>{forceTpl?.reason}. Puoi comunque forzare il richiamo: i livelli verranno caricati ma potrebbero riferirsi a colonne/sorgenti non presenti.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForceTpl(null)} data-testid="model-force-cancel">Annulla</Button>
            <Button onClick={() => doApply(forceTpl.tpl, true)} data-testid="model-force-confirm" className="bg-[hsl(var(--gold)/0.9)] hover:bg-[hsl(var(--gold))] text-black">Forza richiamo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!delTpl} onOpenChange={(o) => { if (!o) setDelTpl(null); }}>
        <DialogContent className="bg-popover border-border max-w-md" data-testid="model-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><TrashSimple size={20} className="text-destructive" /> Elimina modello</DialogTitle>
            <DialogDescription>
              Vuoi eliminare il modello "{delTpl?.name}"? È stato creato il {delTpl ? fmtDate(delTpl.created_at) : ""}. L'operazione non è reversibile.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTpl(null)} data-testid="model-delete-cancel">Annulla</Button>
            <Button variant="destructive" onClick={doDelete} data-testid="model-delete-confirm">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
